import React, { FC, useState, useMemo, SyntheticEvent, ReactNode } from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  AutocompleteRenderGetTagProps,
  TextField,
  Button,
  Grid,
  Chip,
} from '@mui/material';
import { DeviceReadingsSelectorGeneric } from './DeviceReadingsSelector';
import { ReadingsRequestSensorDto, ReadingsRequestSensorTypeDto } from '@thingslog/repositories';
import { SelectedItemsDisplay } from './SelectedItemsDisplay';
import {
  SensorDataDeviceGroupOptions,
  SensorDataDeviceSensorTypeOption,
  SensorDataAllDevicesOption,
  SensorDataDeviceSensorOption,
} from '../../models';
import PeriodPicker from '../../../../components/DatePicker/PeriodPicker';
import { useTranslation } from 'react-i18next';

export const DeviceReadingsSelectorGenericComponent: FC<DeviceReadingsSelectorGeneric> = (
  props: DeviceReadingsSelectorGeneric
) => {
  const { t } = useTranslation();
  const {
    variant,
    payload,
    onPayloadChange,
    onSubmit,
    blockInteractions,
    deviceOptions,
    deviceGroupOptions,
    sensorOptions,
    deviceSensorTypeOptions,
    selectedDeviceNumber,
    onSelectedDeviceNumberChange,
    sensorNameSnapshotter,
  } = props;

  const selectedDevice = useMemo(
    () =>
      deviceOptions.find(
        (option: SensorDataAllDevicesOption) => option.deviceNumber === selectedDeviceNumber
      ) || null,
    [deviceOptions, selectedDeviceNumber]
  );

  const [localSelectedSensor, setLocalSelectedSensor] =
    useState<SensorDataDeviceSensorOption | null>(null);
  const [localSelectedDeviceGroup, setLocalSelectedDeviceGroup] =
    useState<SensorDataDeviceGroupOptions | null>(null);
  const [localSelectedDevices, setLocalSelectedDevices] = useState<SensorDataAllDevicesOption[]>(
    []
  );
  const [localSelectedSensorType, setLocalSelectedSensorType] =
    useState<SensorDataDeviceSensorTypeOption | null>(null);

  // Filter out device groups that are already selected
  const availableDeviceGroupOptions = useMemo(() => {
    return deviceGroupOptions.filter(
      (option: SensorDataDeviceGroupOptions) =>
        !payload.deviceGroupIds.includes(option.deviceGroupId)
    );
  }, [deviceGroupOptions, payload.deviceGroupIds]);

  const handleDeviceChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: SensorDataAllDevicesOption | null
  ): void => {
    if (newValue) {
      onSelectedDeviceNumberChange(newValue.deviceNumber);
    } else {
      onSelectedDeviceNumberChange(null);
    }
    // Clear sensor selection when device changes
    setLocalSelectedSensor(null);
  };

  const handleSensorChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: SensorDataDeviceSensorOption | null
  ): void => {
    setLocalSelectedSensor(newValue);
  };

  const handleDeviceGroupChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: SensorDataDeviceGroupOptions | null
  ): void => {
    setLocalSelectedDeviceGroup(newValue);
  };

  const handleAddDeviceGroup = (): void => {
    if (!localSelectedDeviceGroup) {
      return;
    }

    // Check if this device group already exists
    const alreadyExists = payload.deviceGroupIds.includes(localSelectedDeviceGroup.deviceGroupId);

    if (!alreadyExists) {
      onPayloadChange({
        ...payload,
        deviceGroupIds: [...payload.deviceGroupIds, localSelectedDeviceGroup.deviceGroupId],
      });
    }

    // Clear local device group selection
    setLocalSelectedDeviceGroup(null);
  };

  const handleAdd = (): void => {
    if (!selectedDevice || !localSelectedSensor) {
      return;
    }

    // Check if this device-sensor combination already exists
    const alreadyExists = payload.deviceSensorList.some(
      (sensor: ReadingsRequestSensorDto) =>
        sensor.deviceNumber === selectedDevice.deviceNumber &&
        sensor.sensorIndex === localSelectedSensor.sensorIndex
    );

    if (!alreadyExists) {
      const newSensor: ReadingsRequestSensorDto = {
        deviceNumber: selectedDevice.deviceNumber,
        sensorIndex: localSelectedSensor.sensorIndex,
      };

      onPayloadChange({
        ...payload,
        deviceSensorList: [...payload.deviceSensorList, newSensor],
      });
    }

    // Clear local sensor selection
    setLocalSelectedSensor(null);
  };

  const handleDevicesChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: SensorDataAllDevicesOption[]
  ): void => {
    setLocalSelectedDevices(newValue);
  };

  const handleSensorTypeChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: SensorDataDeviceSensorTypeOption | null
  ): void => {
    setLocalSelectedSensorType(newValue);
  };

  const handleAddDeviceSensorType = (): void => {
    if (localSelectedDevices.length === 0 || !localSelectedSensorType) {
      return;
    }

    const deviceNumbers = localSelectedDevices.map(
      (device: SensorDataAllDevicesOption) => device.deviceNumber
    );

    const newDeviceSensorType: ReadingsRequestSensorTypeDto = {
      deviceNumbers,
      sensorType: localSelectedSensorType.sensorType,
    };

    onPayloadChange({
      ...payload,
      deviceSensorTypeList: [...payload.deviceSensorTypeList, newDeviceSensorType],
    });

    // Clear local selections
    setLocalSelectedDevices([]);
    setLocalSelectedSensorType(null);
  };

  return (
    <Grid container spacing={2} alignItems="flex-start">
      {/* Left Section: Device Group, Device and Sensor Autocompletes with Add Buttons */}
      <Grid item xs={12} md={8}>
        <Grid container spacing={2} alignItems="flex-start">
          {/* Device Group Autocomplete and Add Button */}
          <Grid item xs={12} sm={10}>
            <Autocomplete
              size="small"
              disabled={blockInteractions}
              options={availableDeviceGroupOptions}
              value={localSelectedDeviceGroup}
              onChange={handleDeviceGroupChange}
              getOptionLabel={(option: SensorDataDeviceGroupOptions): string =>
                option.deviceGroupName
              }
              renderInput={(params: AutocompleteRenderInputParams): ReactNode => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('sensors_data_select_device_group')}
                  placeholder={t('sensors_data_search_device_group')}
                />
              )}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              disabled={blockInteractions || !localSelectedDeviceGroup}
              onClick={handleAddDeviceGroup}
              size="medium"
              fullWidth
              disableElevation
            >
              {t('sensors_data_add')}
            </Button>
          </Grid>
          {/* Device and Sensor Autocompletes and Add Button */}
          <Grid item xs={12} sm={5}>
            <Autocomplete
              size="small"
              disabled={blockInteractions}
              options={deviceOptions}
              value={selectedDevice}
              onChange={handleDeviceChange}
              getOptionLabel={(option: SensorDataAllDevicesOption): string =>
                `${option.deviceName} (${option.deviceNumber})`
              }
              renderInput={(params: AutocompleteRenderInputParams): ReactNode => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('sensors_data_select_device')}
                  placeholder={t('sensors_data_search_device')}
                />
              )}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <Autocomplete
              size="small"
              disabled={blockInteractions}
              options={sensorOptions}
              value={localSelectedSensor}
              onChange={handleSensorChange}
              getOptionLabel={(option: SensorDataDeviceSensorOption): string =>
                `${option.sensorName} (Index: ${option.sensorIndex})`
              }
              renderInput={(params: AutocompleteRenderInputParams): ReactNode => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('sensors_data_select_sensor')}
                  placeholder={t('sensors_data_search_sensor')}
                />
              )}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              disabled={blockInteractions || !selectedDevice || !localSelectedSensor}
              onClick={handleAdd}
              size="medium"
              fullWidth
              disableElevation
            >
              {t('sensors_data_add')}
            </Button>
          </Grid>
          {/* Devices Multi-Select and Sensor Type Autocompletes and Add Button */}
          <Grid item xs={12} sm={5}>
            <Autocomplete
              multiple
              size="small"
              disabled={blockInteractions}
              options={deviceOptions}
              value={localSelectedDevices}
              onChange={handleDevicesChange}
              getOptionLabel={(option: SensorDataAllDevicesOption): string =>
                `${option.deviceName} (${option.deviceNumber})`
              }
              renderInput={(params: AutocompleteRenderInputParams): ReactNode => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('sensors_data_select_devices')}
                  placeholder={t('sensors_data_search_device')}
                />
              )}
              renderTags={(
                value: SensorDataAllDevicesOption[],
                getTagProps: AutocompleteRenderGetTagProps
              ): ReactNode =>
                value.map((option: SensorDataAllDevicesOption, index: number) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={`${option.deviceName} (${option.deviceNumber})`}
                      {...tagProps}
                      size="small"
                    />
                  );
                })
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <Autocomplete
              size="small"
              disabled={blockInteractions}
              options={deviceSensorTypeOptions}
              value={localSelectedSensorType}
              onChange={handleSensorTypeChange}
              getOptionLabel={(option: SensorDataDeviceSensorTypeOption): string =>
                t(option.sensorType)
              }
              renderInput={(params: AutocompleteRenderInputParams): ReactNode => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t('sensors_data_select_sensor_type')}
                  placeholder={t('sensors_data_search_sensor_type')}
                />
              )}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              disabled={
                blockInteractions || localSelectedDevices.length === 0 || !localSelectedSensorType
              }
              onClick={handleAddDeviceSensorType}
              size="medium"
              fullWidth
              disableElevation
            >
              {t('sensors_data_add')}
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {/* Right Section: Date Selectors and Submit Button */}
      <Grid item xs={12} md={4}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md>
            <Grid container spacing={2} alignItems="center">
              <PeriodPicker alwaysShowDatePickers />
            </Grid>
          </Grid>
          <Grid item xs={12} md="auto">
            <Button
              variant="contained"
              disabled={blockInteractions}
              onClick={onSubmit}
              size="medium"
              fullWidth
              disableElevation
            >
              {t('sensors_data_submit')}
            </Button>
          </Grid>
          {/* Selected Items Display */}
          <SelectedItemsDisplay
            payload={payload}
            onPayloadChange={onPayloadChange}
            deviceGroupOptions={deviceGroupOptions}
            deviceOptions={deviceOptions}
            sensorNameSnapshotter={sensorNameSnapshotter}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
